import { isEmpty, uniqueId } from 'lodash';
import dayjs from 'dayjs';
import { getArticleUrl, getProfileUrl } from './routingUtils';
import { getImage } from './imageUtils';
import i18n from '~/i18n';
import {
  LINK_PROD,
  HOST_URL,
  LANG_CODE,
  INSTAGRAM_USERNAME,
  FACEBOOK_USERNAME,
  TWITTER_USERNAME,
  YOUTUBE_LINK,
  PINTEREST_USERNAME,
  ARTICLE_PATH,
  SEARCH_PATH,
  SEARCH_ARTICLE_PATH,
  CORPORATE_PATH,
  ABOUT_PATH,
  CAREER_PATH,
  COOKIE_POLICY_PATH,
  PRIVACY_POLICY_PATH,
  USER_AGREEMENT_PATH,
  PUBLICATION_PRINCIPLES_PATH,
  COMMUNITY_RULES_PATH,
  LANG,
} from '~/constants/config';
import { articleDescription } from '~/modules/article/utils/articleUtils';
import { getMainCategories } from '~/modules/category/utils/categoryUtils';

/**
 * returns head object
 * @param {object} schema
 */
export function getSchemaHead(schema) {
  const hid = uniqueId('nuxt-jsonld-');
  const innerHTML = JSON.stringify(schema, null, 2);
  return {
    script: [
      {
        hid,
        type: 'application/ld+json',
        innerHTML,
      },
    ],
    __dangerouslyDisableSanitizersByTagID: {
      [hid]: ['innerHTML'],
    },
  };
}

/**
 * Format author name
 * @param {string | string[]} authorName
 */
function formatAuthorName(authorName, url) {
  if (Array.isArray(authorName)) {
    return authorName.map((name) => ({ '@type': 'Person', name, url }));
  } else {
    return { '@type': 'Person', name: authorName, url };
  }
}

/**
 * Returns image object
 * @param {object} image
 * @param {string} image.url
 * @param {string | number} image.width
 * @param {string | number} image.height
 */
export function formatImage(image) {
  if (isEmpty(image)) return {};
  return {
    '@type': 'ImageObject',
    url: image.url,
    height: image.height?.toString(),
    width: image.width?.toString(),
  };
}

/**
 * Returns publisher
 *
 */
function getPublisher() {
  return {
    publisher: {
      '@type': 'NewsMediaOrganization',
      name: 'Onedio',
      logo: {
        '@type': 'ImageObject',
        url: 'https://onedio.com/images/logo/onedio-new2x-new.png',
        width: '232',
        height: '60',
      },
    },
  };
}

/**
 * Website schema.org
 */
export function generateWebsiteSchema() {
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: LINK_PROD,
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: `${HOST_URL}/${SEARCH_PATH}/${SEARCH_ARTICLE_PATH}/{queryKeyword}`,
        'query-input': 'required name=queryKeyword',
      },
    ],
  };
  return schema;
}

/**
 * NewsMediaOrganization schema.org
 */
export function generateNewsMediaOrganizationSchema() {
  const description = i18n.messages[LANG].meta.description;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    name: 'Onedio',
    description,
    url: LINK_PROD,
    logo: 'https://proj-assets.onedio.com/onediologo.jpg',
    sameAs: [
      `https://www.facebook.com/${FACEBOOK_USERNAME}`,
      `https://twitter.com/${TWITTER_USERNAME}`,
      `https://www.instagram.com/${INSTAGRAM_USERNAME}/`,
      YOUTUBE_LINK,
      'https://www.linkedin.com/company/onedio/',
      `https://pinterest.com/${PINTEREST_USERNAME}/`,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '0 312 969 33 96',
      email: 'onedio@onedio.com',
      contactType: 'İletişim',
    },
  };
  return schema;
}

/**
 * Webpage schema.org
 */
export function generateWebPageSchema({ headline, description }) {
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    headline,
    description,
    inLanguage: LANG_CODE,
  };
  return schema;
}

/**
 * NewsArticle schema.org
 * @param {object} article - article object
 */
export const generateNewsArticleSchema = ({ article }) => {
  const authorName = article.authors?.[0]?.name;
  const articleUrl = `${LINK_PROD}${
    article.mainUrl ? `/${ARTICLE_PATH}/${article.mainUrl}-${article.legacyId}` : `${getArticleUrl({ article })}`
  }`;
  const profileUrl = `${LINK_PROD}${getProfileUrl({ user: article.authors?.[0] })}`;
  const image = formatImage(getImage(article.image, { extraHigh: true }));
  const datePublished = dayjs(article.dates?.published).format('YYYY-MM-DDTHH:mm:ssZ');
  const modifiedData = {};
  if (article.dates?.modified) {
    modifiedData.dateModified = dayjs(article.dates?.modified).format('YYYY-MM-DDTHH:mm:ssZ');
  }
  const keywords = article.keywords || [];

  // article body
  let articleBody = '';
  article.entries.map((entry) => {
    if (entry?.text?.plain) {
      articleBody += entry.text.plain;
    }
  });

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    image,
    headline: article?.title,
    articleBody,
    description: articleDescription(article),
    keywords,
    datePublished,
    ...modifiedData,
    author: formatAuthorName(authorName, profileUrl),
    inLanguage: LANG_CODE,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    articleSection: article?.categories[0]?.name,
    wordCount: articleBody.split(' ').length,
    typicalAgeRange: '7-',
    genre: 'news',
    isFamilyFriendly: 'True',
    publishingPrinciples: `${HOST_URL}/${CORPORATE_PATH}/${PRIVACY_POLICY_PATH}`,
    ...getPublisher(),
  };
  if (article?.seoData?.altTitle) {
    let a = {}; // if altTitle has.
    Object.keys(schema).map((v) =>
      v === 'headline'
        ? (a = { ...a, headline: schema.headline, alternativeHeadline: article.seoData.altTitle })
        : (a = { ...a, [v]: schema[v] })
    );
    return a;
  }
  return schema;
};

/**
 * Tags schema.org
 * @param {array} taglist - tags list
 */
export const generateTagsSchema = (taglist = []) => {
  const itemListElement = taglist?.map((article, index) => ({
    '@type': 'ListItem',
    position: (article?.position || ++index).toString(),
    name: article.title,
    image: article.image?.resolutions?.standard?.url,
    url: `${LINK_PROD}${article.mainUrl ? `/${ARTICLE_PATH}/${article.mainUrl}-${article.legacyId}` : `${getArticleUrl({ article })}`}`,
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement,
  };
  return schema;
};

/**
 * Category schema.org
 * @param {array} categorylist - category list
 */
export const generateCategorySchema = (categorylist = []) => {
  const itemListElement = categorylist?.map((article, index) => ({
    '@type': 'ListItem',
    position: (article?.position || ++index).toString(),
    item: {
      '@type': 'NewsArticle',
      headline: article.title,
      image: [article.image?.resolutions?.standard?.url],
      url: `${LINK_PROD}${article.mainUrl ? `/${ARTICLE_PATH}/${article.mainUrl}-${article.legacyId}` : `${getArticleUrl({ article })}`}`,
      datePublished: dayjs(article.dates?.published).format('YYYY-MM-DDTHH:mm:ssZ'),
      dateModified: dayjs(article.dates?.updated).format('YYYY-MM-DDTHH:mm:ssZ'),
      author: [
        {
          '@type': 'Person',
          name: article.author?.name,
          url: `${LINK_PROD}${getProfileUrl({ user: article.author })}`,
        },
      ],
    },
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement,
  };
  return schema;
};

/**
 * Video Category schema.org
 * @param {array} videolist - video list
 */
export const generateVideoCategorySchema = (videolist = []) => {
  const itemListElement = videolist?.map((article, index) => ({
    '@type': 'ListItem',
    position: (article?.position || ++index).toString(),
    item: {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: article.title,
      description: articleDescription(article),
      thumbnailUrl: article.image?.resolutions?.standard?.url,
      url: `${LINK_PROD}${article.mainUrl ? `/${ARTICLE_PATH}/${article.mainUrl}-${article.legacyId}` : `${getArticleUrl({ article })}`}`,
      uploadDate: dayjs(article.dates?.published).format('YYYY-MM-DD'),
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/CommentAction',
        userInteractionCount: article?.stats?.views,
      },
    },
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement,
  };
  return schema;
};

/**
 * Video schema.org
 * @param {object} entry
 * @param {object} article
 */
export function generateVideoSchema(entry, article) {
  const videoUrl = entry?.videoData?.url.replace('hls.m3u8', '720p.mp4');
  const videoEntries = article.entries?.filter((e) => e.mode === 'video');
  const videoIndex = videoEntries?.findIndex((e) => e._id === entry._id);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: videoIndex > 0 ? entry.title || article.title : article.title,
    description: articleDescription(article),
    thumbnailUrl: entry?.image?.resolutions?.extraHigh?.url || entry?.thumbnailUrl,
    uploadDate: dayjs(article.dates?.updated).format('YYYY-MM-DDTHH:mm:ssZ'),
    contentUrl: videoUrl,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: article?.stats?.views || 0,
    },
  };
  return schema;
}

/**
 * Breadcrumb schema.org
 * @param {Object[]} elements
 * @param {string} elements[].name
 * @param {string} elements[].url
 * @param {string} [elements[].position] - optional
 */
export function generateBreadcrumbSchema(elements) {
  const itemListElement = elements.map((item, index) => ({
    '@type': 'ListItem',
    position: (item?.position || ++index).toString(),
    name: item.name,
    item: item.url,
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };

  return schema;
}

/**
 * Article Breadcrumb for schema.org
 * @param {object} article
 */
export function generateArticleBreadcrumbSchema({ article }) {
  const breadcrumbRoot = i18n.messages[LANG].main.breadcrumbRoot;
  const breadcrumbs = [{ name: breadcrumbRoot, url: LINK_PROD }];
  const category = article.breadcrumb;
  category && category.map((cat) => breadcrumbs.push({ name: cat.name, url: `${LINK_PROD}${cat.route}` }));
  const articleUrl = `${LINK_PROD}${
    article.mainUrl ? `/${ARTICLE_PATH}/${article.mainUrl}-${article.legacyId}` : `${getArticleUrl({ article })}`
  }`;
  breadcrumbs.push({ name: article.title, url: articleUrl });
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  return breadcrumbSchema;
}

/**
 * LocalBusinesses schema.org
 */
export function generateLocalBussinessSchema() {
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: 'Onedio',
    url: LINK_PROD,
    sameAs: [
      'onedio.com',
      `https://www.facebook.com/${FACEBOOK_USERNAME}`,
      `https://twitter.com/${TWITTER_USERNAME}`,
      `https://www.instagram.com/${INSTAGRAM_USERNAME}/`,
      YOUTUBE_LINK,
      'https://news.google.com/publications/CAAqBwgKMIGmrAsw_rDEAw?ceid=TR:tr&oc=3&hl=tr&gl=TR',
      'https://tr.wikipedia.org/wiki/Onedio',
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ankara',
      addressRegion: 'TR',
      postalCode: '06510',
      streetAddress: 'Mustafa Kemal Mah. Dumlupınar Bulv. No:280 (Yeni ODTÜ Teknokent Binası) Kat:4 No:2 06530 Çankaya/Ankara',
    },
    telephone: '0 312 969 33 96',
    email: 'onedio@onedio.com',
    logo: 'https://onedio.com/images/logo/onedio-new2x-new.png',
  };

  return schema;
}

/**
 * Contact Page schema.org
 */
export function generateContactSchema() {
  const contact = i18n.messages[LANG].corporate.contact;
  const description = i18n.messages[LANG].meta.contact;
  const schema = {
    '@graph': [
      generateNewsMediaOrganizationSchema(),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        headline: contact + ' | Onedio',
        description,
        inLanguage: LANG_CODE,
        relatedLink: [
          `${HOST_URL}/${CORPORATE_PATH}/${ABOUT_PATH}`,
          `${HOST_URL}/${CORPORATE_PATH}/${CAREER_PATH}`,
          `${HOST_URL}/${COOKIE_POLICY_PATH}`,
          `${HOST_URL}/${CORPORATE_PATH}/${PRIVACY_POLICY_PATH}`,
          `${HOST_URL}/${CORPORATE_PATH}/${USER_AGREEMENT_PATH}`,
          `${HOST_URL}/${CORPORATE_PATH}/${PUBLICATION_PRINCIPLES_PATH}`,
          `${HOST_URL}/${CORPORATE_PATH}/${COMMUNITY_RULES_PATH}`,
        ],
      },
    ],
  };
  return schema;
}

/**
 * Feed Category schema.org
 */
export function generateSiteNavigationElementSchema() {
  const categoryList = getMainCategories().map((cat) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      '@id': '#header',
      name: cat.name,
      url: HOST_URL + cat.href,
    };
  });
  const schema = {
    '@graph': [
      {
        '@context': 'https://schema.org',
        '@graph': categoryList,
      },
    ],
  };
  return schema;
}

/**
 * FAQ schema.org
 * @param {array} faqlist - question list
 */
export const generateFaqSchema = (faqlist = []) => {
  const itemListElement = faqlist?.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: itemListElement,
  };
  return schema;
};
